<template>
  <div ref="raiz" class="combobox">
    <button type="button" class="combobox-botao" :aria-expanded="aberto" @click="alternar">
      <span><b v-if="label">{{ label }}: </b>{{ rotuloSelecionado || placeholder }}</span>
      <i class="fa-solid fa-chevron-down" aria-hidden="true"></i>
    </button>

    <div v-if="aberto" class="combobox-lista">
      <input v-model="busca" type="search" class="combobox-busca" placeholder="Pesquisar..." @input="aoDigitar" />
      <ul role="listbox">
        <li role="option" :class="{ ativo: !modelValue }" @click="escolher(null)">{{ placeholder }}</li>
        <li v-for="item in itens" :key="item.id" role="option" :class="{ ativo: item.id === modelValue }"
          @click="escolher(item)">
          {{ item.rotulo }}
        </li>
        <li v-if="carregando" class="estado">Carregando...</li>
        <li v-else-if="!itens.length" class="estado">Nenhum resultado</li>
        <li v-else-if="temMais" class="mais" @click.stop="carregar()">Carregar mais</li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from "vue";

// buscarPagina({ page, busca }) -> { itens: [{ id, rotulo }], ultima: boolean }
const props = defineProps({
  buscarPagina: { type: Function, required: true },
  label: { type: String, default: "" },
  placeholder: { type: String, default: "Todos" },
  // muda quando a lista depende de outro filtro (ex.: unidade) e precisa recarregar
  dependencia: { type: [String, Number], default: "" },
});
const modelValue = defineModel({ default: "" });

const raiz = ref(null);
const aberto = ref(false);
const busca = ref("");
const itens = ref([]);
const pagina = ref(0);
const temMais = ref(false);
const carregando = ref(false);
const rotuloSelecionado = ref("");
let carregouUmaVez = false;
let timer;

async function carregar(reiniciar = false) {
  if (carregando.value) return;
  if (reiniciar) {
    pagina.value = 0;
    itens.value = [];
  }
  carregando.value = true;
  try {
    const r = await props.buscarPagina({ page: pagina.value, busca: busca.value.trim() });
    itens.value.push(...r.itens);
    temMais.value = !r.ultima;
    pagina.value++;
  } catch (e) {
    console.error("Erro ao carregar opções:", e);
    temMais.value = false;
  } finally {
    carregando.value = false;
    carregouUmaVez = true;
  }
}

function alternar() {
  aberto.value = !aberto.value;
  // só consulta o backend na primeira abertura
  if (aberto.value && !carregouUmaVez) carregar(true);
}

function aoDigitar() {
  clearTimeout(timer);
  timer = setTimeout(() => carregar(true), 300);
}

function escolher(item) {
  modelValue.value = item ? item.id : "";
  rotuloSelecionado.value = item ? item.rotulo : "";
  aberto.value = false;
}

// dependência mudou: lista antiga não vale mais e a seleção também
watch(() => props.dependencia, () => {
  itens.value = [];
  carregouUmaVez = false;
  busca.value = "";
  if (modelValue.value) escolher(null);
  if (aberto.value) carregar(true);
});

// seleção limpa de fora (ex.: "limpar filtros")
watch(modelValue, (v) => { if (!v) rotuloSelecionado.value = ""; });

function fecharFora(e) {
  if (raiz.value && !raiz.value.contains(e.target)) aberto.value = false;
}
onMounted(() => document.addEventListener("click", fecharFora));
onBeforeUnmount(() => {
  document.removeEventListener("click", fecharFora);
  clearTimeout(timer);
});
</script>

<style scoped>
.combobox {
  position: relative;
  width: 185px;
}

.combobox-botao {
  display: flex;
  width: 100%;
  min-height: 42px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid var(--roxo-superficie);
  border-radius: 9px;
  color: var(--cinza-escuro);
  background: var(--branco);
  font-size: 0.82rem;
  text-align: left;
  cursor: pointer;
}

.combobox-botao span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.combobox-lista {
  position: absolute;
  z-index: 20;
  top: 100%;
  left: 0;
  width: 260px;
  max-width: 90vw;
  padding: 8px;
  border: 1px solid var(--roxo-superficie);
  border-radius: 9px;
  background: var(--branco);
  box-shadow: 0 6px 20px hsla(258, 55%, 15%, 0.12);
}

.combobox-busca {
  width: 100%;
  min-height: 36px;
  padding: 6px 10px;
  border: 1px solid var(--roxo-superficie);
  border-radius: 8px;
  font-size: 0.8rem;
  outline: none;
}

ul {
  max-height: 220px;
  margin: 6px 0 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
}

li {
  padding: 8px 10px;
  border-radius: 6px;
  color: var(--cinza-escuro);
  font-size: 0.8rem;
  cursor: pointer;
}

li:hover,
li.ativo {
  background: var(--roxo-superficie);
}

li.estado {
  color: var(--cinza-medio);
  cursor: default;
}

li.mais {
  color: var(--roxo-medio);
  font-weight: 700;
  text-align: center;
}
</style>
